<!--
  LibraryColorwayPreviewSelect -- "Preview colorway" drop-down on the
  decoration library artwork detail page, next to "Edit thread colorways".

  VIEW-ONLY. Choosing a colorway changes the picture on this page and
  nothing else: it is not persisted, it binds nothing, and the only request
  it can make that is not a GET is the same stateless Wilcom recolor render
  the colorway editor uses (POST /wilcom/artwork/<id>/colorway-render). That
  route returns a data: URL; it uploads nothing to Artifi or object storage.
  No second renderer exists here by design.

  COST DISCIPLINE.
    - Nothing is fetched until the user opens the menu, so a plain page view
      spends no Wilcom call.
    - A colorway is rendered at most once per (id, updated_at); re-choosing
      it, or flipping back from Default, reuses the cached image.
    - The list comes from the same two reads the editor makes: the artwork
      read for its current version id, then that version's saved colorways.

  Emits:
    preview({ url, colorwayId } | null)  null = show the default preview
    busy(Boolean)                        a render is in flight
-->
<template>
    <div class="lcps" data-testid="library-colorway-preview">
        <v-select
            :model-value="selectedId"
            :items="items"
            item-title="title"
            item-value="value"
            label="Preview colorway"
            density="compact"
            variant="outlined"
            hide-details
            :disabled="disabled || !isStitch"
            :loading="loadingList || rendering"
            :aria-describedby="noteText ? noteId : undefined"
            data-testid="library-colorway-preview-select"
            @update:menu="onMenu"
            @update:model-value="onPick"
        />
        <span
            v-if="noteText"
            :id="noteId"
            class="lcps__note"
            data-testid="library-colorway-preview-note"
        >
            {{ noteText }}
        </span>
        <span
            v-else-if="noneSaved"
            class="lcps__note"
            data-testid="library-colorway-preview-none"
        >
            No saved colorways yet. Use Edit thread colorways to add one.
        </span>
        <span
            v-if="message"
            class="lcps__error"
            role="alert"
            data-testid="library-colorway-preview-error"
        >
            {{ message }}
        </span>
    </div>
</template>

<script>
import api from '@/services/api'
import { usableSavedThreads } from './savedColorwayThreads'

const STITCH_FILE = /\.(dst|emb)$/i
// The v-select value for "Default". Not null: Vuetify renders a null model
// as an EMPTY field, and Default must read as a real, selected entry.
const DEFAULT = 'default'

export default {
    name: 'LibraryColorwayPreviewSelect',
    props: {
        artworkId: { type: [String, Number], required: true },
        filename: { type: String, default: '' },
        // The page has no stitch preview to override yet (pending/failed).
        disabled: { type: Boolean, default: false },
        // Why the PAGE disabled it (pending preview, no permission), shown
        // as helper text so a dead control is never unexplained.
        disabledNote: { type: String, default: '' },
    },
    emits: ['preview', 'busy'],
    data() {
        return {
            // DEFAULT === the design's own thread colors.
            selectedId: DEFAULT,
            saved: [],
            // The design's own current colorway from the artwork read:
            // { name, threads: palette[], stops: [{stop_index, color_idx}] }.
            // A recolor is sent per PALETTE slot, so it is the base a saved
            // (per-stop) colorway is laid over.
            design: null,
            versionId: null,
            listLoaded: false,
            loadingList: false,
            rendering: false,
            message: '',
            // key -> data: URL. Plain (non-reactive) bookkeeping.
            renders: new Map(),
            pickSeq: 0,
        }
    },
    computed: {
        noteId() {
            return `lcps-note-${this.artworkId}`
        },
        noteText() {
            if (!this.isStitch) {
                return 'Thread colorways apply to .dst or .emb artwork only.'
            }
            return this.disabled ? this.disabledNote : ''
        },
        isStitch() {
            return STITCH_FILE.test(this.filename || '')
        },
        items() {
            return [
                { title: 'Default', value: DEFAULT },
                ...this.saved.map((c) => ({ title: c.name, value: c.id })),
            ]
        },
        noneSaved() {
            return this.listLoaded && !this.saved.length
        },
    },
    beforeUnmount() {
        // The page unmounts this control while it refetches (loading state),
        // which skips the watchers below. Tell the page the preview and busy
        // state are gone so it cannot keep showing a recolor, or a spinner,
        // that no control owns any more.
        this.pickSeq++
        this.$emit('preview', null)
        this.$emit('busy', false)
    },
    watch: {
        // A different artwork is a different list and a different cache.
        artworkId() {
            this.reset()
        },
        filename() {
            this.reset()
        },
    },
    methods: {
        reset() {
            this.pickSeq++
            this.selectedId = DEFAULT
            this.saved = []
            this.design = null
            this.versionId = null
            this.listLoaded = false
            this.loadingList = false
            this.renders = new Map()
            this.message = ''
            this.setRendering(false)
            this.$emit('preview', null)
        },
        setRendering(on) {
            this.rendering = on
            this.$emit('busy', on)
        },
        async onMenu(open) {
            if (open) await this.loadList()
        },
        async loadList() {
            if (this.listLoaded || this.loadingList || !this.isStitch) return
            this.loadingList = true
            this.message = ''
            const forArtwork = this.artworkId
            try {
                const { data } = await api.get(
                    `/wilcom/artwork/${this.artworkId}/colorways`,
                )
                const versionId = data?.source?.artwork_version_id
                const designs = (data && data.colorways) || []
                const current = designs.find((c) => c.current) || designs[0]
                if (data?.supported === false || !versionId) {
                    if (forArtwork === this.artworkId) {
                        this.saved = []
                        this.listLoaded = true
                    }
                    return
                }
                const res = await api.get(
                    `/wilcom/artwork-versions/${versionId}/colorways`,
                )
                if (forArtwork !== this.artworkId) return
                this.design = current || null
                this.versionId = versionId
                this.saved = (res.data && res.data.colorways) || []
                this.listLoaded = true
            } catch (err) {
                if (forArtwork === this.artworkId) {
                    this.message =
                        'Could not load colorways. Showing the default colors.'
                }
            } finally {
                if (forArtwork === this.artworkId) this.loadingList = false
            }
        },
        // Thread list for the recolor route, exactly as the editor builds it:
        // one entry per PALETTE slot of the design's colorway, in order,
        // because Wilcom recolors the whole colorway and a partial or
        // mis-sized list is refused upstream (502). A saved colorway is keyed
        // by STOP (the sewn sequence), and a stop's color_idx names the
        // palette slot it sews, so each saved thread is laid onto its slot;
        // slots no stop overrides keep the design's own thread. First stop
        // to claim a slot wins. Returns null when the design's palette is
        // unknown (nothing faithful to send).
        threadsFor(colorway) {
            const palette = this.design && this.design.threads
            if (!Array.isArray(palette) || !palette.length) return null
            const stops = Array.isArray(this.design.stops)
                ? this.design.stops
                : []
            const out = palette.map((t) => ({
                hex: t.hex,
                code: t.code || '',
                brand: t.brand || '',
                description: t.description || '',
            }))
            if (out.some((t) => !t.hex)) return null
            const claimed = new Set()
            for (const saved of usableSavedThreads(colorway)) {
                const stop = stops.find((s) => s.stop_index === saved.stop_index)
                const slot = stop
                    ? stop.color_idx
                    : stops.length
                      ? undefined
                      : (saved.stop_index ?? 0) - 1
                if (slot === undefined || slot === null) continue
                if (!out[slot] || claimed.has(slot)) continue
                claimed.add(slot)
                out[slot] = {
                    hex: saved.hex,
                    code: saved.code,
                    brand: saved.brand || '',
                    description: out[slot].description,
                }
            }
            return out
        },
        fallBackToDefault(text) {
            this.selectedId = DEFAULT
            this.message = text
            this.$emit('preview', null)
        },
        async onPick(id) {
            const seq = ++this.pickSeq
            this.message = ''
            if (id === DEFAULT || id === null || id === undefined) {
                this.selectedId = DEFAULT
                this.setRendering(false)
                this.$emit('preview', null)
                return
            }
            const colorway = this.saved.find((c) => c.id === id)
            if (!colorway) {
                this.fallBackToDefault(
                    'That colorway is not available. Showing the default colors.',
                )
                return
            }
            this.selectedId = id
            const key = `${colorway.id}:${colorway.updated_at || ''}:${this.versionId}`
            const cached = this.renders.get(key)
            if (cached) {
                this.setRendering(false)
                this.$emit('preview', { url: cached, colorwayId: id })
                return
            }
            const threads = this.threadsFor(colorway)
            if (!threads) {
                this.fallBackToDefault(
                    `"${colorway.name}" cannot be previewed for this design. ` +
                        'Showing the default colors.',
                )
                return
            }
            this.setRendering(true)
            try {
                const { data } = await api.post(
                    `/wilcom/artwork/${this.artworkId}/colorway-render`,
                    { colorway: this.design.name || colorway.name, threads },
                )
                if (seq !== this.pickSeq) return
                const url = data && data.image_url
                if (!url) throw new Error('no image')
                this.renders.set(key, url)
                this.$emit('preview', { url, colorwayId: id })
            } catch (err) {
                if (seq !== this.pickSeq) return
                this.fallBackToDefault(
                    'Could not preview that colorway. Showing the default colors.',
                )
            } finally {
                if (seq === this.pickSeq) this.setRendering(false)
            }
        },
    },
}
</script>

<style scoped>
.lcps {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 10px 0 2px;
}
.lcps__note {
    color: var(--tf-fg-3, #546e7a);
    font-size: 0.75rem;
}
.lcps__error {
    color: var(--tf-error-dark, #b3261e);
    font-size: 0.75rem;
}
</style>
